import { faEllipsisVertical, faStar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactElement, useState } from 'react'
import { ActionsMenu } from '~/components/Global/Interactions/ActionsMenu/ActionsMenu'
import { ActionsMenuItem } from '~/components/Global/Interactions/ActionsMenu/ActionsMenuItem'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import type { SavedSearch } from '~/domains/common/types'
import {
  savedSearchChip,
  savedSearchDefaultIcon,
  savedSearchMoreBtn,
  savedSearchRow
} from './FilterBar.css'
import { normalizeQueryString } from './utils'

type SavedSearchChipsProps = {
  searches: SavedSearch[]
  activeQueryString: string
  onSelect: (queryString: string) => void
  onDelete: (index: number) => void
  onSetDefault: (index: number) => void
}

const SavedSearchChipItem = ({
  search,
  index,
  isActive,
  onSelect,
  onDelete,
  onSetDefault
}: {
  search: SavedSearch
  index: number
  isActive: boolean
  onSelect: () => void
  onDelete: (index: number) => void
  onSetDefault: (index: number) => void
}): ReactElement => {
  const [showMenuTrigger, setShowMenuTrigger] = useState(false)
  return (
    <Button
      variation={isActive ? ButtonVariation.Primary : ButtonVariation.Secondary}
      buttonSize="extraSmall"
      onClick={onSelect}
      onMouseEnter={() => setShowMenuTrigger(true)}
      onMouseLeave={() => setShowMenuTrigger(false)}
      className={savedSearchChip}
    >
      <AnimatePresence>
        {search.isDefault && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, width: 0 }}
            animate={{ opacity: 1, scale: 1, width: 'auto' }}
            exit={{ opacity: 0, scale: 0.8, width: 0 }}
          >
            <FontAwesomeIcon className={savedSearchDefaultIcon} icon={faStar} />
          </motion.div>
        )}
      </AnimatePresence>
      {search.name}
      <AnimatePresence>
        {showMenuTrigger && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ActionsMenu
              trigger={(props) => (
                <span {...props}>
                  <FontAwesomeIcon
                    className={savedSearchMoreBtn}
                    icon={faEllipsisVertical}
                  />
                </span>
              )}
            >
              <ActionsMenuItem
                title={
                  search.isDefault ? 'Retirer par défaut' : 'Définir par défaut'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  onSetDefault(index)
                }}
              />
              <ActionsMenuItem
                title="Supprimer"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(index)
                }}
              />
            </ActionsMenu>
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  )
}

export const SavedSearchChips = ({
  searches,
  activeQueryString,
  onSelect,
  onDelete,
  onSetDefault
}: SavedSearchChipsProps): ReactElement | null => {
  if (searches.length === 0) return null

  return (
    <div className={savedSearchRow}>
      <AnimatePresence initial={false} mode="popLayout">
        {searches.map((search, index) => (
          <motion.div
            key={search.queryString}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <SavedSearchChipItem
              search={search}
              index={index}
              isActive={
                normalizeQueryString(search.queryString) === activeQueryString
              }
              onSelect={() => onSelect(search.queryString)}
              onDelete={onDelete}
              onSetDefault={onSetDefault}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
